import { useState, useEffect } from 'react';
import api from '../../utils/api';

export default function SectionManager({ courseId }) {
  const [sections, setSections] = useState([]);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ title: '', sortOrder: 0, minTimeMinutes: 30 });
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const load = async () => {
    setLoading(true);
    try {
      const [sectionsRes, slidesRes] = await Promise.all([
        api.get(`/admin/courses/${courseId}/sections`),
        api.get(`/admin/courses/${courseId}/slides`),
      ]);
      setSections(sectionsRes.data.sections);
      setSlides(slidesRes.data.slides);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [courseId]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/admin/courses/${courseId}/sections/${editId}`, form);
      } else {
        await api.post(`/admin/courses/${courseId}/sections`, form);
      }
      setShowForm(false);
      setEditId(null);
      setForm({ title: '', sortOrder: sections.length, minTimeMinutes: 30 });
      setMessage('Section saved!');
      setTimeout(() => setMessage(''), 3000);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section? Slides will be unassigned.')) return;
    await api.delete(`/admin/courses/${courseId}/sections/${sectionId}`);
    load();
  };

  const startEdit = (section) => {
    setEditId(section.id);
    setForm({ title: section.title, sortOrder: section.sort_order, minTimeMinutes: section.min_time_minutes || 30 });
    setShowForm(true);
  };

  const assignSlideToSection = async (slideId, sectionId) => {
    await api.put(`/admin/courses/${courseId}/slides/${slideId}`, {
      sectionId: sectionId || null,
    });
    load();
  };

  const getSectionSlides = (sectionId) => slides.filter(s => s.section_id === sectionId);
  const unassignedSlides = slides.filter(s => !s.section_id);

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <h3 style={styles.title}>📂 Section Manager</h3>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditId(null); setForm({ title:'', sortOrder:sections.length, minTimeMinutes:30 }); setShowForm(!showForm); }}>
          {showForm ? 'Cancel' : '+ Add Section'}
        </button>
      </div>

      <p style={styles.desc}>
        Organize slides into sections. Each section has a minimum time requirement — students must spend that time in the section before taking the quiz and advancing.
      </p>

      {message && <div className="alert alert-success" style={{ marginBottom:16 }}>{message}</div>}

      {showForm && (
        <div style={styles.formBox}>
          <h4 style={{ marginBottom:14, fontWeight:700 }}>{editId ? 'Edit Section' : 'New Section'}</h4>
          <form onSubmit={save} style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ display:'flex', gap:12 }}>
              <div className="form-group" style={{ flex:2 }}>
                <label className="form-label">Section Title *</label>
                <input className="input" placeholder="e.g. Introduction, Agency Relationships" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} required />
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Min Time (minutes) *</label>
                <input className="input" type="number" min="1" value={form.minTimeMinutes} onChange={e => setForm(f=>({...f,minTimeMinutes:parseInt(e.target.value)||30}))} required />
              </div>
              <div className="form-group" style={{ flex:1 }}>
                <label className="form-label">Sort Order</label>
                <input className="input" type="number" value={form.sortOrder} onChange={e => setForm(f=>({...f,sortOrder:parseInt(e.target.value)||0}))} />
              </div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-primary" type="submit">{editId ? 'Update' : 'Create Section'}</button>
              <button className="btn btn-ghost" type="button" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
        <div>
          {sections.length === 0 ? (
            <div style={styles.empty}>No sections yet. Create a section to organize your slides.</div>
          ) : (
            sections.map(section => {
              const sectionSlides = getSectionSlides(section.id);
              return (
                <div key={section.id} style={styles.sectionCard}>
                  <div style={{ ...styles.sectionHeader, cursor:'pointer' }} onClick={() => toggleSection(section.id)}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontSize:14, color:'var(--slate-400)', transition:'transform 0.2s', transform: expandedSections[section.id] ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                      <div style={styles.sectionInfo}>
                        <div style={styles.sectionTitle}>{section.title}</div>
                        <div style={styles.sectionMeta}>
                          <span style={styles.timeBadge}>⏱ {section.min_time_minutes} min required</span>
                          <span style={styles.slideCount}>{sectionSlides.length} slides</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6 }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-ghost btn-sm" onClick={() => startEdit(section)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteSection(section.id)}>Delete</button>
                    </div>
                  </div>

                  {/* Slides in this section — collapsible */}
                  {expandedSections[section.id] && (
                    <div style={styles.slidesList}>
                      {sectionSlides.length === 0 ? (
                        <div style={styles.noSlides}>No slides assigned to this section yet.</div>
                      ) : sectionSlides.map((slide, i) => (
                        <div key={slide.id} style={styles.slideRow}>
                          <span style={styles.slideNum}>{i+1}</span>
                          <span style={styles.slideTitle}>{slide.title || 'Untitled'}</span>
                          <button style={styles.removeBtn} onClick={() => assignSlideToSection(slide.id, null)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  root: { marginTop: 24 },
  header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 },
  title: { fontSize:18, fontWeight:700, color:'var(--slate-900)' },
  desc: { fontSize:13, color:'var(--color-text-muted)', marginBottom:16, lineHeight:1.5 },
  formBox: { background:'var(--tan-50)', border:'1.5px solid #b39e89', borderRadius:12, padding:'20px 24px', marginBottom:20 },
  empty: { padding:32, textAlign:'center', color:'var(--color-text-muted)', fontSize:14, background:'var(--slate-50)', borderRadius:10, border:'1px dashed var(--color-border)' },
  sectionCard: { background:'white', border:'1.5px solid #b39e89', borderRadius:12, padding:'18px 20px', marginBottom:16 },
  sectionHeader: { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12, gap:16 },
  sectionInfo: {},
  sectionTitle: { fontSize:16, fontWeight:700, color:'var(--forest-700)', marginBottom:6 },
  sectionMeta: { display:'flex', gap:12, alignItems:'center' },
  timeBadge: { background:'var(--forest-50)', color:'var(--forest-700)', border:'1px solid var(--forest-200)', borderRadius:999, padding:'3px 10px', fontSize:12, fontWeight:600 },
  slideCount: { fontSize:12, color:'var(--color-text-muted)', fontWeight:500 },
  slidesList: { display:'flex', flexDirection:'column', gap:6, marginBottom:12 },
  noSlides: { fontSize:13, color:'var(--color-text-muted)', fontStyle:'italic', padding:'8px 0' },
  slideRow: { display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'var(--slate-50)', borderRadius:8, border:'1px solid var(--color-border)' },
  slideNum: { width:22, height:22, background:'var(--forest-500)', color:'white', borderRadius:'50%', fontSize:11, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  slideTitle: { flex:1, fontSize:13, color:'var(--slate-700)' },
  removeBtn: { background:'none', border:'none', color:'var(--color-error)', fontSize:12, cursor:'pointer', fontWeight:600, flexShrink:0 },
  addSlides: { borderTop:'1px solid var(--color-border)', paddingTop:10 },
  addSelect: { width:'100%', padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--color-border)', fontSize:13, color:'var(--slate-700)', background:'white', cursor:'pointer', outline:'none' },
  unassignedBox: { background:'var(--slate-50)', border:'1px dashed var(--color-border)', borderRadius:12, padding:'16px 20px', marginTop:16 },
  unassignedTitle: { fontSize:14, fontWeight:700, color:'var(--slate-700)', marginBottom:6 },
  unassignedDesc: { fontSize:13, color:'var(--color-text-muted)', marginBottom:12 },
};